import { useState } from 'react'
import { useSelector } from 'react-redux'
import Chevron from 'assets/icons/Chevron'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearAccommodationInfo, getAccommodationStatistics, getCamps } from 'features/accommodation/accommodationSlice'
import { RootState } from 'store'
import { IGetAllCampsSingleResponse } from 'types/api/accommodationApi.interface'
import { NoData } from 'components/NoData'
import { tokens } from 'styling/tokens'
import { AddAccommodationModal } from './AddAccommodationModal'
import {
  SectionLabel,
  SectionCount,
  CampList,
  CampCard,
  CampHeader,
  CampIdentity,
  CampName,
  CampMeta,
  CampKpis,
  CampKpi,
  CampKpiValue,
  CampKpiLabel,
  ChevronBox,
  CampCollapse,
  CampCollapseInner,
} from './AccommodationCamp.styled'
import {
  HeaderActions,
  IconBtn,
  RoomList,
  RoomGroup,
  RoomHeader,
  RoomTitle,
  RoomName,
  RoomContext,
  RoomCount,
  BedChips,
  BedChip,
  BedChipDot,
  BedChipLabel,
  BedChipState,
  RoomEmpty,
} from './BedManagement.styled'

const stateInfo = (state: number): { label: string; color: string } => {
  switch (state) {
    case 1:
      return { label: 'Available', color: tokens.status.success.fg }
    case 2:
      return { label: 'Occupied', color: tokens.status.info.fg }
    case 3:
      return { label: 'Dirty', color: tokens.status.warning.fg }
    case 4:
      return { label: 'Blocked', color: tokens.status.danger.fg }
    default:
      return { label: 'Unknown', color: tokens.border.strong }
  }
}

const genderLabel = (g: number) => (g === 1 ? 'Male' : g === 2 ? 'Female' : g === 3 ? 'Both' : 'Any')
const tierLabel = (t: number) =>
  t === 1 ? 'VIP' : t === 2 ? 'Senior' : t === 3 ? 'Junior' : t === 4 ? 'Contractor' : 'Any'

interface RoomGroupData {
  id: number
  name: string
  blockName: string
  gender: number
  tier: number
  beds: { id: number; label: string; state: number }[]
  occupied: number
}

const computeCamp = (camp: IGetAllCampsSingleResponse) => {
  let rooms = 0
  let beds = 0
  let available = 0
  let occupied = 0
  let dirty = 0
  const roomGroups: RoomGroupData[] = []

  ;(camp.blocks || []).forEach((block) => {
    ;(block.rooms || []).forEach((room) => {
      rooms += 1
      let roomOccupied = 0
      ;(room.beds || []).forEach((bed) => {
        beds += 1
        if (bed.state === 1) available += 1
        else if (bed.state === 2) {
          occupied += 1
          roomOccupied += 1
        } else if (bed.state === 3) dirty += 1
      })
      roomGroups.push({
        id: room.id,
        name: room.label,
        blockName: block.name,
        gender: block.genderPolicy,
        tier: block.tierPolicy,
        beds: room.beds || [],
        occupied: roomOccupied,
      })
    })
  })

  return {
    rooms,
    beds,
    available,
    occupied,
    dirty,
    occupancy: beds ? Math.round((occupied / beds) * 100) : 0,
    roomGroups,
  }
}

const PencilIcon = () => (
  <svg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M11.4 2.1l2.5 2.5L5.6 12.9l-3 .5.5-3 8.3-8.3z' stroke='currentColor' strokeWidth='1.2' strokeLinejoin='round' />
  </svg>
)

const CampGroup = ({
  camp,
  active,
  onToggle,
  onEdit,
}: {
  camp: IGetAllCampsSingleResponse
  active: boolean
  onToggle: () => void
  onEdit: () => void
}) => {
  const stats = computeCamp(camp)

  return (
    <CampCard $active={active}>
      <CampHeader onClick={onToggle}>
        <CampIdentity>
          <CampName>{camp.name}</CampName>
          <CampMeta>
            {camp.blocks?.length || 0} blocks · {stats.rooms} rooms · {stats.beds} beds
          </CampMeta>
        </CampIdentity>
        <CampKpis>
          <CampKpi>
            <CampKpiValue $color={tokens.status.info.fg}>{stats.occupancy}%</CampKpiValue>
            <CampKpiLabel>Occupancy</CampKpiLabel>
          </CampKpi>
          <CampKpi>
            <CampKpiValue $color={tokens.status.success.fg}>{stats.available}</CampKpiValue>
            <CampKpiLabel>Available</CampKpiLabel>
          </CampKpi>
          <CampKpi>
            <CampKpiValue $color={stats.dirty ? tokens.status.warning.fg : tokens.text.secondary}>{stats.dirty}</CampKpiValue>
            <CampKpiLabel>Cleaning</CampKpiLabel>
          </CampKpi>
          <HeaderActions>
            <IconBtn
              type='button'
              aria-label='Edit camp'
              onClick={(e) => {
                e.stopPropagation()
                onEdit()
              }}
            >
              <PencilIcon />
            </IconBtn>
            <ChevronBox>
              <Chevron open={active} />
            </ChevronBox>
          </HeaderActions>
        </CampKpis>
      </CampHeader>

      <CampCollapse $open={active}>
        <CampCollapseInner>
          <RoomList>
            {stats.roomGroups.length ? (
              stats.roomGroups.map((room) => (
                <RoomGroup key={room.id}>
                  <RoomHeader>
                    <RoomTitle>
                      <RoomName>{room.name}</RoomName>
                      <RoomContext>
                        {room.blockName} · {genderLabel(room.gender)} · {tierLabel(room.tier)}
                      </RoomContext>
                    </RoomTitle>
                    <RoomCount>
                      {room.occupied}/{room.beds.length} occupied
                    </RoomCount>
                  </RoomHeader>
                  {room.beds.length ? (
                    <BedChips>
                      {room.beds.map((bed) => {
                        const info = stateInfo(bed.state)
                        return (
                          <BedChip key={bed.id} $accent={info.color}>
                            <BedChipDot $color={info.color} />
                            <BedChipLabel>{bed.label}</BedChipLabel>
                            <BedChipState>{info.label}</BedChipState>
                          </BedChip>
                        )
                      })}
                    </BedChips>
                  ) : (
                    <RoomEmpty>No beds in this room</RoomEmpty>
                  )}
                </RoomGroup>
              ))
            ) : (
              <RoomEmpty>No rooms configured for this camp</RoomEmpty>
            )}
          </RoomList>
        </CampCollapseInner>
      </CampCollapse>
    </CampCard>
  )
}

export const BedManagement = () => {
  const [params] = useState({ page: 1, limit: 99 })
  const [openCamp, setOpenCamp] = useState<number | null>(null)
  const [editCamp, setEditCamp] = useState<IGetAllCampsSingleResponse | null>(null)
  const { camps } = useSelector((store: RootState) => store.accomodation)

  useQueryPagination({
    fetchFunctions: [getCamps, getAccommodationStatistics],
    params,
    cleanUpFunctions: [clearAccommodationInfo],
  })

  const campsData = camps?.data || []

  return (
    <>
      <SectionLabel>
        Camps <SectionCount>{campsData.length}</SectionCount>
      </SectionLabel>
      <CampList>
        {campsData.length ? (
          campsData.map((camp) => (
            <CampGroup
              key={camp.id}
              camp={camp}
              active={openCamp === camp.id}
              onToggle={() => setOpenCamp((prev) => (prev === camp.id ? null : camp.id))}
              onEdit={() => setEditCamp(camp)}
            />
          ))
        ) : (
          <NoData />
        )}
      </CampList>
      {editCamp && <AddAccommodationModal editCamp={editCamp} closeModal={() => setEditCamp(null)} />}
    </>
  )
}
